export interface WebSocketConfig {
  url: string
  protocols?: string | string[]
  heartbeatInterval?: number
}

interface ResolvedWebSocketConfig {
  url: string
  protocols?: string | string[]
  heartbeatInterval: number
}

export type WebSocketMessageHandler<T = unknown> = (data: T) => void
export type WebSocketErrorHandler = (error: Event | Error) => void
export type WebSocketConnectionHandler = () => void

export class WebSocketService<T = unknown> {
  private ws: WebSocket | null = null
  private config: ResolvedWebSocketConfig
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null
  private messageHandlers: Set<WebSocketMessageHandler<T>> = new Set()
  private errorHandlers: Set<WebSocketErrorHandler> = new Set()
  private openHandlers: Set<WebSocketConnectionHandler> = new Set()
  private closeHandlers: Set<WebSocketConnectionHandler> = new Set()

  constructor(config: WebSocketConfig) {
    this.config = {
      url: config.url,
      protocols: config.protocols,
      heartbeatInterval: config.heartbeatInterval ?? 30000
    }
  }

  connect(): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return
    }

    if (this.ws?.readyState === WebSocket.CONNECTING) {
      return
    }

    try {
      this.ws = new WebSocket(this.config.url, this.config.protocols)
      this.setupEventListeners()
    } catch (error) {
      this.handleError(error as Error)
    }
  }

  disconnect(): void {
    this.clearHeartbeat()

    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect')
      this.ws = null
    }
  }

  send(data: string | ArrayBufferLike | Blob | ArrayBufferView): void {
    if (!this.ws) {
      return
    }

    if (this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(data)
    }
  }

  onMessage(handler: WebSocketMessageHandler<T>): () => void {
    this.messageHandlers.add(handler)
    return () => this.messageHandlers.delete(handler)
  }

  onError(handler: WebSocketErrorHandler): () => void {
    this.errorHandlers.add(handler)
    return () => this.errorHandlers.delete(handler)
  }

  onOpen(handler: WebSocketConnectionHandler): () => void {
    this.openHandlers.add(handler)
    return () => this.openHandlers.delete(handler)
  }

  onClose(handler: WebSocketConnectionHandler): () => void {
    this.closeHandlers.add(handler)
    return () => this.closeHandlers.delete(handler)
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  private setupEventListeners(): void {
    if (!this.ws) return

    this.ws.onopen = this.handleOpen.bind(this)
    this.ws.onmessage = this.handleMessage.bind(this)
    this.ws.onerror = this.handleError.bind(this)
    this.ws.onclose = this.handleClose.bind(this)
  }

  private handleOpen(): void {
    this.startHeartbeat()
    this.openHandlers.forEach((handler) => handler())
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data) as T
      this.messageHandlers.forEach((handler) => handler(data))
    } catch (error) {
      this.handleError(error as Error)
    }
  }

  private handleError(error: Event | Error): void {
    this.errorHandlers.forEach((handler) => handler(error))
  }

  private handleClose(): void {
    this.clearHeartbeat()
    this.closeHandlers.forEach((handler) => handler())
  }

  private startHeartbeat(): void {
    this.clearHeartbeat()
    if (this.config.heartbeatInterval > 0) {
      this.heartbeatInterval = setInterval(() => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.send(JSON.stringify({ type: 'ping' }))
        }
      }, this.config.heartbeatInterval)
    }
  }

  private clearHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }
}
